import makeStyles from '@mui/styles/makeStyles';
import { useQueryClient } from '@tanstack/react-query';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import TableRefreshHeaderFeature from 'src/app/components/features/tables/TableRefreshHeaderFeature';
import TicketLinkImportRowTable from 'src/app/components/tables/TicketLinkImportRowTable';
import { useApplyTicketLinkImport } from 'src/app/hooks/ticket-link-imports/useApplyTicketLinkImport';
import {
    FETCH_TICKET_LINK_IMPORT_QUERY_KEY,
    useFetchTicketLinkImport,
} from 'src/app/hooks/ticket-link-imports/useFetchTicketLinkImport';
import {
    FETCH_TICKET_LINK_IMPORT_ROWS_QUERY_KEY,
    useFetchTicketLinkImportRows,
} from 'src/app/hooks/ticket-link-imports/useFetchTicketLinkImportRows';
import { FETCH_TICKET_LINK_IMPORTS_QUERY_KEY } from 'src/app/hooks/ticket-link-imports/useFetchTicketLinkImports';
import { useGetTicketLinkDuplicateTicket } from 'src/app/hooks/ticket-link-imports/useGetTicketLinkDuplicateTicket';
import { useRematchTicketLinkImport } from 'src/app/hooks/ticket-link-imports/useRematchTicketLinkImport';
import EventUrlService from 'src/app/pages/events/eventUrlService';
import { TicketLinkImportStatus } from 'src/data/models/ticket-link-import/TicketLinkImport';
import { useSpacingStyles } from 'src/shared/styles/spacingStyles';
import Button from 'src/view/components/button/Button';
import CheckboxWithLabel from 'src/view/components/checkbox-with-label/CheckboxWithLabel';
import HelpPopover from 'src/view/components/help-popover/HelpPopover';

export interface TicketLinkImportRowTableFeatureProps {
    eventId: string;
    ticketLinkImportId: string;
    failedRowsOnly: boolean;
    bookingOptionName: string;
}

const REMATCHING_PHASE_STATUSES = new Set<TicketLinkImportStatus>([
    'Pending',
    'PartiallyMatched',
    'PartiallyApplied',
]);

const REAPPLYING_PHASE_STATUSES = new Set<TicketLinkImportStatus>([
    'Applying',
    'PartiallyApplied',
    'Pending',
    'Matched',
]);

const useStyles = makeStyles(() => ({
    buttonsContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
    },
}));

export default function TicketLinkImportRowTableFeature(
    props: Readonly<TicketLinkImportRowTableFeatureProps>
): ReactElement {
    const [page, setPage] = useState<number>(1);
    const [acceptFailedRows, setAcceptFailedRows] = useState<boolean>(false);

    const { data: importData, isFetching: importFetching } = useFetchTicketLinkImport(
        props.ticketLinkImportId
    );
    const importStatus = importData?.data.data.status ?? null;

    useEffect(() => {
        setPage(1);
    }, [props.failedRowsOnly]);

    useEffect(() => {
        if (importStatus === 'Pending' && acceptFailedRows) {
            setAcceptFailedRows(false);
        }
    }, [importStatus, acceptFailedRows]);

    const {
        data: fetchResult,
        isLoading: loading,
        isFetching: fetching,
    } = useFetchTicketLinkImportRows(props.ticketLinkImportId, props.failedRowsOnly, {
        page,
    });

    const { mutate: getTicketLinkDuplicateTicket } = useGetTicketLinkDuplicateTicket((result) => {
        const { eventId, ticketId } = result.data.data;

        window.open(
            EventUrlService.tickets(eventId, {
                ticketId: ticketId,
            }),
            '_blank'
        );
    });

    const classes = useStyles();
    const spacingClasses = useSpacingStyles();

    const queryClient = useQueryClient();

    const importRows = fetchResult?.data.data;
    const responseMeta = fetchResult?.data.meta;
    const statusCounts = responseMeta?.statusCounts as
        | Partial<
              Record<'pending' | 'matched' | 'matchingFailed' | 'applied' | 'applyFailed', number>
          >
        | undefined;

    const matchingFailedCount = statusCounts?.matchingFailed ?? 0;
    const applyFailedCount = statusCounts?.applyFailed ?? 0;
    const appliedCount = statusCounts?.applied ?? 0;
    const matchedCount = statusCounts?.matched ?? 0;

    const invalidateQueries = useCallback(() => {
        queryClient.invalidateQueries({
            queryKey: [FETCH_TICKET_LINK_IMPORTS_QUERY_KEY, props.ticketLinkImportId],
            exact: false,
        });
        queryClient.invalidateQueries({
            queryKey: [FETCH_TICKET_LINK_IMPORT_ROWS_QUERY_KEY, props.ticketLinkImportId],
            exact: false,
        });
        queryClient.invalidateQueries({
            queryKey: [FETCH_TICKET_LINK_IMPORT_QUERY_KEY, props.ticketLinkImportId],
            exact: false,
        });
    }, [queryClient, props.ticketLinkImportId]);

    const { mutate: applyImport, isLoading: isApplyLoading } =
        useApplyTicketLinkImport(invalidateQueries);

    const { mutate: rematchImport, isLoading: isRematchLoading } =
        useRematchTicketLinkImport(invalidateQueries);

    const hasMatchingFailedRows = matchingFailedCount > 0;
    const hasAppliedRows = appliedCount > 0;
    const hasApplyFailedRows = applyFailedCount > 0;
    const hasMatchedRows = matchedCount > 0;

    const isInRematchingPhase =
        importStatus !== null &&
        REMATCHING_PHASE_STATUSES.has(importStatus) &&
        hasMatchingFailedRows;

    const isInReapplyingPhase =
        importStatus !== null &&
        REAPPLYING_PHASE_STATUSES.has(importStatus) &&
        (hasApplyFailedRows || hasAppliedRows);

    const canRematch =
        isInRematchingPhase && !isRematchLoading && !importFetching && importStatus !== 'Pending';
    const canApply =
        hasMatchedRows &&
        !isApplyLoading &&
        importStatus !== 'Applying' &&
        (!hasMatchingFailedRows || acceptFailedRows);
    const canRetryApply =
        (hasApplyFailedRows || hasMatchedRows) && !isApplyLoading && importStatus !== 'Applying';

    return (
        <div className={spacingClasses.spacingTop}>
            <TableRefreshHeaderFeature
                title={`Import rows - ${props.bookingOptionName}`}
                queryKeys={[
                    [FETCH_TICKET_LINK_IMPORT_ROWS_QUERY_KEY, props.ticketLinkImportId],
                    [FETCH_TICKET_LINK_IMPORT_QUERY_KEY, props.ticketLinkImportId],
                ]}
            />

            <TicketLinkImportRowTable
                ticketLinkImportRows={importRows ?? []}
                onClickDuplicateFile={getTicketLinkDuplicateTicket}
                loading={loading || fetching}
                eventId={props.eventId}
                pagination={{
                    currentPage: responseMeta?.currentPage || 1,
                    totalPages: responseMeta?.totalPages || 1,
                    onPaginate: setPage,
                }}
            />

            {isInRematchingPhase && (
                <div className={`${classes.buttonsContainer} ${spacingClasses.spacingTop}`}>
                    <Button
                        disabled={!canRematch}
                        onClick={() => rematchImport(props.ticketLinkImportId)}
                    >
                        Rematch failed row(s)
                    </Button>
                </div>
            )}

            <div className={`${classes.buttonsContainer} ${spacingClasses.spacingTop}`}>
                {(importStatus === 'PartiallyMatched' ||
                    (importStatus === 'Pending' && hasMatchingFailedRows)) && (
                    <CheckboxWithLabel
                        label="Accept failed rows"
                        checked={acceptFailedRows}
                        onChange={() => setAcceptFailedRows(!acceptFailedRows)}
                    />
                )}

                {isInReapplyingPhase ? (
                    <Button
                        disabled={!canRetryApply}
                        onClick={() => applyImport(props.ticketLinkImportId)}
                    >
                        Retry apply
                        <HelpPopover
                            muted
                            content={{
                                text: 'Retry rows with status Matched and ApplyFailed',
                            }}
                        />
                    </Button>
                ) : (
                    <Button
                        disabled={!canApply}
                        onClick={() => applyImport(props.ticketLinkImportId)}
                    >
                        Apply
                    </Button>
                )}
            </div>
        </div>
    );
}
